import React, { useState, useRef } from 'react';
import Select from 'react-select';
import IntlTelInput from 'intl-tel-input/react';
import 'intl-tel-input/styles';

function Preview({ fields, rtl, submitButtonText, submitButtonColor, submitButtonTextColor }) {
  const [formData, setFormData] = useState({});
  const [fileData, setFileData] = useState({});
  const telInputRef = useRef({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFileData({
      ...fileData,
      [name]: files[0],
    });
  };

  const handleSelectChange = (name, selectedOptions) => {
    let selectedValues;
    if (Array.isArray(selectedOptions)) {
      selectedValues = selectedOptions.map(option => option.value).join(',');
    } else {
      selectedValues = selectedOptions?.value || '';
    }
    setFormData({
      ...formData,
      [name]: selectedValues,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submissionData = new FormData();

    // Add regular form data
    for (const key in formData) {
      submissionData.append(key, formData[key]);
    }

    // Add file data
    for (const key in fileData) {
      submissionData.append(key, fileData[key]);
    }

    // Add phone numbers
    for (const key in telInputRef.current) {
      if (telInputRef.current[key]?.getInstance) {
        const instance = telInputRef.current[key].getInstance();
        const fullNumber = instance?.getNumber();
        if (fullNumber) {
          submissionData.append(key, fullNumber);
        }
      }
    }

    // Display form data in console (for demo purposes)
    console.log('Form submitted with data:');
    for (const [key, value] of submissionData.entries()) {
      console.log(`${key}: ${value}`);
    }

    // Here you would typically send the data to your server
    alert('Form submitted! Check console for form data.');
  };

  const renderInputField = (field) => {
    const commonInputProps = {
      name: field.name,
      className: "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500",
      required: field.isRequired,
      onChange: handleInputChange,
    };

    if (field.type === 'number') {
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
          <input
            type="number"
            min={field.hasRange ? field.minValue : undefined}
            max={field.hasRange ? field.maxValue : undefined}
            {...commonInputProps}
          />
        </div>
      );
    } else {
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
          <input
            type={field.type}
            {...commonInputProps}
          />
        </div>
      );
    }
  };

  const renderPhoneField = (field) => {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
        <IntlTelInput
          ref={el => telInputRef.current[field.name] = el}
          inputProps={{
            name: field.name,
            required: field.isRequired,
            className: "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500",
            dir: rtl ? 'rtl' : 'ltr'
          }}
          initOptions={{
            initialCountry: "us",
            preferredCountries: ['us', 'gb', 'ca', 'au'],
            separateDialCode: true,
            utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js"
          }}
        />
      </div>
    );
  };

  const renderCheckBoxField = (field) => {
    return (
      <div className="mb-4 flex items-center">
        <input
          type="checkbox"
          id={field.name}
          name={field.name}
          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          onChange={handleInputChange}
        />
        <label htmlFor={field.name} className="ml-2 block text-sm text-gray-700">
          {field.label}
        </label>
      </div>
    );
  };

  const renderFileField = (field) => {
    const accept = field.fileExtensions.map(ext => ext.value).join(',');
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
        <input
          type="file"
          name={field.name}
          accept={accept}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          required={field.isRequired}
          onChange={handleFileChange}
        />
      </div>
    );
  };

  const renderSelectField = (field) => {
    const options = field.options.map(option => ({ value: option.value, label: option.value }));
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
        <Select
          isMulti={field.allowMultipleValue}
          name={field.name}
          options={options}
          className="basic-multi-select"
          classNamePrefix="select"
          onChange={(selectedOptions) => handleSelectChange(field.name, selectedOptions)}
        />
      </div>
    );
  };

  const renderRadioButtonField = (field) => {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
        <div className="space-y-2">
          {field.choices.map((choice) => (
            <div key={choice.id} className="flex items-center">
              <input
                type="radio"
                id={`${field.name}-${choice.id}`}
                name={field.name}
                value={choice.value}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                onChange={handleInputChange}
              />
              <label htmlFor={`${field.name}-${choice.id}`} className="ml-2 text-sm text-gray-700">
                {choice.value}
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMultipleField = (field) => {
    return (
      <div className="mb-4">
        <div className={`grid grid-cols-1 md:grid-cols-${field.elementNumber} gap-4`}>
          {field.nestedFields.map((nestedField) => {
            const renderFunction = nameToRenderFunctionMap[nestedField.field];
            return renderFunction ? (
              <div key={nestedField.id}>
                {renderFunction(nestedField)}
              </div>
            ) : null;
          })}
        </div>
      </div>
    );
  };

  const renderTextAreaField = (field) => {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
        <textarea
          name={field.name}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          required={field.isRequired}
          onChange={handleInputChange}
          rows={4}
        ></textarea>
      </div>
    );
  };

  const nameToRenderFunctionMap = {
    'InputField': renderInputField,
    'EmailField': renderInputField,
    'FirstNameField': renderInputField,
    'LastNameField': renderInputField,
    'PhoneField': renderPhoneField,
    'CheckBoxField': renderCheckBoxField,
    'FileField': renderFileField,
    'SelectField': renderSelectField,
    'RadioButtonField': renderRadioButtonField,
    'MultipleField': renderMultipleField,
    'TextAreaField': renderTextAreaField,
  };

  return (
    <div dir={rtl ? 'rtl' : 'ltr'} className="py-4">
      <form onSubmit={handleSubmit}>
        {fields.map((field) => {
          const renderFunction = nameToRenderFunctionMap[field.field];
          return renderFunction ? (
            <div key={field.id}>
              {renderFunction(field)}
            </div>
          ) : null;
        })}
        
        {fields.length > 0 && (
          <button
            type="submit"
            style={{ 
              backgroundColor: submitButtonColor || '#4f46e5', 
              color: submitButtonTextColor || '#ffffff' 
            }}
            className="mt-4 px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {submitButtonText || 'Submit'}
          </button>
        )}
      </form>
    </div>
  );
}

export default Preview;